<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  recommendationAddress,
  recommendationDateTimeLabel,
  recommendationHouseId,
  visitAddressLine,
  visitDateTimeLabel,
  VISIT_DETAIL_TEXT,
} from '../utils/visitDetailPresenter';

const props = defineProps({
  show: { type: Boolean, default: false },
  visit: { type: Object, default: () => ({}) },
  recommendations: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'confirm']);

const closeLabel = 'Fechar revisão de agendamento';
const currentVisitLabel = 'Visita atual';
const newVisitLabel = 'Nova visita';
const backLabel = 'Voltar';

const alertDescription = computed(() =>
  props.recommendations.length === 1
    ? 'Ao confirmar, a nova visita vai ser agendada.'
    : VISIT_DETAIL_TEXT.EXTRA_VISITS_CONFIRM_DESCRIPTION
);

const canConfirm = computed(
  () => props.recommendations.length > 0 && !props.saving
);
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-lais-modal flex items-end bg-slate-950/40 p-0 sm:p-4"
      @click.self="emit('close')"
    >
      <section
        role="dialog"
        aria-modal="true"
        :aria-label="VISIT_DETAIL_TEXT.EXTRA_VISITS_CONFIRM_TITLE"
        class="max-h-[92vh] w-full overflow-hidden rounded-t-lais-xl border border-lais-border bg-white shadow-lais-pop sm:mx-auto sm:max-w-[720px] sm:rounded-lais-xl"
      >
        <header
          class="flex items-start justify-between gap-3 border-b border-lais-border px-5 py-4 sm:px-6"
        >
          <h2 class="text-[20px] font-semibold leading-tight text-lais-text">
            {{ VISIT_DETAIL_TEXT.EXTRA_VISITS_CONFIRM_TITLE }}
          </h2>
          <button
            type="button"
            class="grid size-9 shrink-0 place-items-center rounded-full text-lais-text-muted hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus"
            :aria-label="closeLabel"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </header>

        <div class="max-h-[calc(92vh-148px)] overflow-y-auto px-5 py-5 sm:px-6">
          <ol
            class="relative grid gap-3 pl-5 before:absolute before:left-[7px] before:top-3 before:h-[calc(100%-1.5rem)] before:w-px before:bg-lais-border"
          >
            <li
              class="relative grid gap-1 rounded-lais-sm bg-lais-bg-soft px-4 py-3"
            >
              <span
                class="absolute -left-[22px] top-4 size-3 rounded-full border-2 border-white bg-lais-primary"
                aria-hidden="true"
              />
              <span
                class="text-[11px] font-bold uppercase text-lais-text-muted"
              >
                {{ currentVisitLabel }}
              </span>
              <strong class="text-[14px] leading-snug text-lais-text">
                {{ visitDateTimeLabel(visit) }}
              </strong>
              <span class="text-[13px] leading-snug text-lais-text-body">
                {{ visitAddressLine(visit) }}
              </span>
            </li>

            <li
              v-for="item in recommendations"
              :key="`extra-review-${recommendationHouseId(item)}`"
              class="relative grid gap-1 rounded-lais-sm border border-lais-border bg-white px-4 py-3"
            >
              <span
                class="absolute -left-[22px] top-4 size-3 rounded-full border-2 border-white bg-lais-primary"
                aria-hidden="true"
              />
              <span class="text-[11px] font-bold uppercase text-lais-primary">
                {{ newVisitLabel }}
              </span>
              <strong class="text-[14px] leading-snug text-lais-text">
                {{ recommendationDateTimeLabel(item) }}
              </strong>
              <span class="text-[13px] leading-snug text-lais-text-body">
                {{ recommendationAddress(item) }}
              </span>
            </li>
          </ol>

          <div
            class="mt-4 rounded-lais-sm border border-lais-primary/20 bg-lais-primary-50/40 px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
          >
            {{ alertDescription }}
          </div>
        </div>

        <footer class="border-t border-lais-border px-5 py-4 sm:px-6">
          <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <LaisPrimaryButton
              variant="ghost"
              :disabled="saving"
              @click="emit('close')"
            >
              {{ backLabel }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-calendar-check"
              :is-loading="saving"
              :disabled="!canConfirm"
              @click="emit('confirm')"
            >
              {{ VISIT_DETAIL_TEXT.EXTRA_VISITS_CONFIRM_BUTTON }}
            </LaisPrimaryButton>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>
